"use client";

import React, { FC, MouseEvent } from "react";
import { useRouter } from "next/navigation";
import { useStyles } from "./styles";

import { applyFirstEditionDefaults } from "utils/cta";
import LockIcon from "@mui/icons-material/Lock";
import SecurityIcon from "@mui/icons-material/Security";
import ShareIcon from "@mui/icons-material/Share";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ShieldIcon from "@mui/icons-material/Shield";

export interface PrivateViewPaywallProps {
  onClose: () => void;
  onUpgrade: () => void;
  versionIdentifier?: string;
  ctaContent?: {
    modalTitle?: string;
    modalSubtitle?: string;
    feature1Title?: string;
    feature1Desc?: string;
    feature2Title?: string;
    feature2Desc?: string;
    feature3Title?: string;
    feature3Desc?: string;
    feature4Title?: string;
    feature4Desc?: string;
    feature5Title?: string;
    feature5Desc?: string;
  };
}

const PrivateViewPaywall: FC<PrivateViewPaywallProps> = ({
  onClose,
  onUpgrade,
  versionIdentifier,
  ctaContent,
}) => {
  const { classes } = useStyles();
  const router = useRouter();

  const handleCancel = () => {
    onClose();
  };

  const handleUpgrade = () => {
    onUpgrade();
    router.push("/subscribe");
  };

  const defaults = {
    modalTitle: "Subscription Required",
    modalSubtitle: "Upgrade to make your creations private and secure",
    feature1Title: "Complete Privacy & Control",
    feature1Desc:
      "Lock down your gallery. Choose exactly who can see your images.",
    feature2Title: "Protect Your Intellectual Property",
    feature2Desc: "Prevent unauthorized access to your ideas.",
    feature3Title: "Selective Sharing",
    feature3Desc: "Share with specific people while keeping others private.",
    feature4Title: "Hidden Drafts",
    feature4Desc:
      "Keep work-in-progress images private until they're ready to reveal.",
    feature5Title: "Premium Privacy Protection",
    feature5Desc:
      "Your gallery is currently public. Upgrade now to keep everything confidential by default.",
  };

  const content = applyFirstEditionDefaults(
    versionIdentifier,
    "A",
    defaults,
    ctaContent
  );

  return (
    <div className={classes.overlay} onClick={handleCancel}>
      <div
        className={classes.modal}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <div className={classes.modalHeader}>
          <div className={classes.securityIcon}>
            <div className={classes.shield}></div>
            <div className={classes.lock}></div>
          </div>
          <div className={classes.headerText}>
            <h2 className={classes.modalTitle}>{content.modalTitle}</h2>
            <p className={classes.modalSubtitle}>{content.modalSubtitle}</p>
          </div>
        </div>

        <div className={classes.privacyBenefits}>
          <div className={classes.benefitCard}>
            <div className={classes.benefitIcon}>
              <LockIcon fontSize="small" style={{ color: "#f52d5a" }} />
            </div>
            <div className={classes.benefitContent}>
              <h4 className={classes.benefitTitle}>{content.feature1Title}</h4>
              <p className={classes.benefitDesc}>{content.feature1Desc}</p>
            </div>
          </div>

          <div className={classes.benefitCard}>
            <div className={classes.benefitIcon}>
              <SecurityIcon fontSize="small" style={{ color: "#f52d5a" }} />
            </div>
            <div className={classes.benefitContent}>
              <h4 className={classes.benefitTitle}>{content.feature2Title}</h4>
              <p className={classes.benefitDesc}>{content.feature2Desc}</p>
            </div>
          </div>

          <div className={classes.benefitCard}>
            <div className={classes.benefitIcon}>
              <ShareIcon fontSize="small" style={{ color: "#f52d5a" }} />
            </div>
            <div className={classes.benefitContent}>
              <h4 className={classes.benefitTitle}>{content.feature3Title}</h4>
              <p className={classes.benefitDesc}>{content.feature3Desc}</p>
            </div>
          </div>

          <div className={classes.benefitCard}>
            <div className={classes.benefitIcon}>
              <VisibilityOffIcon
                fontSize="small"
                style={{ color: "#f52d5a" }}
              />
            </div>
            <div className={classes.benefitContent}>
              <h4 className={classes.benefitTitle}>{content.feature4Title}</h4>
              <p className={classes.benefitDesc}>{content.feature4Desc}</p>
            </div>
          </div>
        </div>

        <div className={classes.privacyLevel}>
          <div className={classes.levelIcon}>
            <ShieldIcon fontSize="small" style={{ color: "#ffffff" }} />
          </div>
          <div className={classes.levelContent}>
            <h4 className={classes.levelTitle}>{content.feature5Title}</h4>
            <p className={classes.levelDesc}>{content.feature5Desc}</p>
          </div>
        </div>

        <div className={classes.modalActions}>
          <button
            className={`${classes.btn} ${classes.btnCancel}`}
            onClick={handleCancel}
          >
            CANCEL
          </button>
          <button
            className={`${classes.btn} ${classes.btnUpgrade}`}
            onClick={handleUpgrade}
          >
            UPGRADE NOW
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivateViewPaywall;
